/* BP-014 — Strategy Comparison Engine tab.
   Full deterministic ranking with Goal Profile selector, per-strategy factor
   contribution breakdown, save/publish/PDF lifecycle. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Trophy, RefreshCw, ChevronDown, ChevronUp,
  Save, ShieldCheck, Download, AlertCircle, AlertTriangle,
} from "lucide-react";
import { StrategyProfitBar } from "@/components/v3/PropertyCharts";

const STRATEGY_TAB = {
  wholesale:       "deal_intelligence",
  fixflip:         "fixflip",
  rental:          "rental",
  brrrr:           "brrrr",
  wholetail:       "wholetail",
  seller_finance:  "seller_finance",
  subject_to:      "subject_to",
  novation:        "novation",
};

const FACTOR_LABEL = {
  projected_profit:  "Profit",
  monthly_cash_flow: "Cash Flow",
  roi:               "ROI",
  cash_efficiency:   "Low Cash",
  speed:             "Speed",
  risk:              "Low Risk",
};

export default function StrategyCompareTab({ property, onNavigateTab }) {
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("balanced");
  const [comparison, setComparison] = useState(null);   // {ranked, winner_id, ...}
  const [current, setCurrent] = useState(null);         // saved snapshot (if any)
  const [loading, setLoading] = useState(true);
  const [calcing, setCalcing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [comparisonName, setComparisonName] = useState("Base Comparison");
  const [expanded, setExpanded] = useState({});

  const loadProfiles = useCallback(async () => {
    try {
      const { data } = await api.get(`/v3/strategy-compare/profiles`);
      setProfiles(data.profiles || []);
    } catch { /* ignore */ }
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/strategy-comparisons/latest`);
      if (data) {
        setCurrent(data);
        setProfileId(data.profile_id);
        setComparisonName(data.comparison_name);
      }
    } catch { /* ignore */ }
  }, [property.id]);

  const calc = useCallback(async (save = false) => {
    save ? setSaving(true) : setCalcing(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/strategy-comparisons/calculate`,
        { goal_profile: profileId, save, comparison_name: comparisonName });
      if (save) {
        setCurrent(data);
        setComparison({
          ranked:          data.rankings.map(mapRankingToRanked),
          winner_id:       data.winner_id,
          runner_up_id:    data.runner_up_id,
          profile_id:      data.profile_id,
          profile_weights: data.profile_weights,
          eligible_count:  data.eligible_count,
          ineligible:      data.ineligible,
        });
        toast.success(`Comparison v${data.version} saved`);
      } else {
        setComparison(data);
        toast.success(`Calculated · winner: ${data.winner_id || "none"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Calculation failed");
    } finally {
      save ? setSaving(false) : setCalcing(false);
    }
  }, [property.id, profileId, comparisonName]);

  const publish = useCallback(async () => {
    if (!current?.id) { toast.error("Save the comparison first."); return; }
    setPublishing(true);
    try {
      const { data } = await api.post(`/v3/strategy-comparisons/${current.id}/publish`, {});
      setCurrent(data);
      toast.success(`Comparison v${data.version} published`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  }, [current]);

  const exportPdf = useCallback(() => {
    if (!current?.id) { toast.error("Save first to export."); return; }
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/strategy-comparisons/${current.id}/export/pdf`, "_blank");
  }, [current]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadProfiles(), loadLatest()]);
      setLoading(false);
    })();
  }, [loadProfiles, loadLatest]);

  // Auto-calc on profile change (unless we're loading).
  useEffect(() => {
    if (loading) return;
    calc(false);
  }, [profileId, loading, calc]);

  const ranked = comparison?.ranked || [];
  const eligible = ranked.filter((r) => r.eligible);
  const ineligible = ranked.filter((r) => !r.eligible);
  const winner = ranked.find((r) => r.id === comparison?.winner_id) || null;
  const weights = comparison?.profile_weights || {};
  const isPublished = Boolean(current?.published_at);
  const isStale = Boolean(current?.stale);

  const activeProfile = profiles.find((p) => p.id === profileId);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="sc-loading">
      <Loader2 size={14} className="animate-spin"/> Loading strategy comparison…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-strategy-compare">
      {/* Header + controls */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sc-controls">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block md:col-span-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Goal Profile</div>
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)}
                    data-testid="sc-profile-select"
                    className="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100">
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.system ? "" : " (custom)"}
                </option>
              ))}
            </select>
          </label>
          <label className="block md:col-span-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Comparison Name</div>
            <input value={comparisonName} onChange={(e) => setComparisonName(e.target.value)}
                    data-testid="sc-name"
                    className="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"/>
          </label>
          <div className="flex flex-wrap items-end gap-2">
            <button onClick={() => calc(false)} disabled={calcing} data-testid="sc-recalc"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
              {calcing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Recalculate
            </button>
            <button onClick={() => calc(true)} disabled={saving || calcing} data-testid="sc-save"
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save
            </button>
            <button onClick={publish} disabled={publishing || !current?.id || isPublished || !comparison?.winner_id}
                    data-testid="sc-publish"
                    title={isPublished ? "Already published" : !current?.id ? "Save first" : !comparison?.winner_id ? "No eligible strategy" : "Publish"}
                    className="inline-flex items-center gap-1 rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-500 disabled:opacity-50">
              {publishing ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={12}/>}
              {isPublished ? "Published" : "Publish"}
            </button>
            <button onClick={exportPdf} disabled={!current?.id} data-testid="sc-pdf"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
              <Download size={12}/> PDF
            </button>
          </div>
        </div>
        {activeProfile && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
            <span>Weights:</span>
            {Object.entries(activeProfile.weights || {}).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 tabular-nums">
                {FACTOR_LABEL[k] || k}: {Math.round((v || 0) * 100)}%
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Stale banner */}
      {isStale && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-center gap-2"
              data-testid="sc-stale">
          <AlertTriangle size={14}/>
          <span>This comparison is <b>stale</b> — one or more source analyses have been updated. Recalculate and save a new version.</span>
        </div>
      )}

      {/* Winner banner */}
      <div className="rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="sc-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-amber-800/80">
              <Trophy size={12}/> Recommended Strategy under &ldquo;{activeProfile?.name || profileId}&rdquo;
            </div>
            {winner ? (
              <>
                <div className="mt-1 text-3xl font-bold text-slate-900" data-testid="sc-winner-label">
                  {winner.label}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  Weighted Score <b className="tabular-nums">{winner.final_score?.toFixed?.(1) ?? winner.final_score}</b>/100
                </div>
                {winner.reason && (
                  <div className="text-[11px] text-slate-600 mt-1 italic">{winner.reason}</div>
                )}
              </>
            ) : (
              <div className="mt-1 text-slate-700 text-sm">
                No eligible strategies. Save at least one strategy scenario to compare.
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Eligible</div>
            <div className="text-2xl font-bold tabular-nums text-slate-900" data-testid="sc-coverage">
              {comparison?.eligible_count ?? 0}<span className="text-sm text-slate-500 font-medium"> / 8</span>
            </div>
            {current?.version && (
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">
                v{current.version} · {isPublished ? "PUBLISHED" : "DRAFT"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Colorful profit-comparison bar chart */}
      {eligible.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-6" data-testid="sc-profit-chart">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Projected Profit</div>
          <div className="text-sm font-semibold text-slate-900 mb-3">
            Winning strategy in emerald · scaled to the deal&apos;s highest exit
          </div>
          <StrategyProfitBar ranked={eligible} winnerId={comparison?.winner_id} />
        </div>
      )}

      {/* Ranking table (with expandable rows) */}
      {eligible.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="sc-table">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 px-4 pt-3 pb-1">
            Ranked · Ineligible strategies at bottom
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <Th>#</Th>
                  <Th>Strategy</Th>
                  <Th align="right">Score</Th>
                  <Th align="right">Profit</Th>
                  <Th align="right">CF/mo</Th>
                  <Th align="right">Cash Req</Th>
                  <Th align="right">Confidence</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((r, idx) => {
                  const isOpen = expanded[r.id];
                  const raw = r.raw_factors || {};
                  return (
                    <React.Fragment key={r.id}>
                      <tr className={`border-t border-slate-100 hover:bg-slate-50 ${r.is_winner || r.id === comparison?.winner_id ? "bg-amber-50/60" : ""} ${!r.eligible ? "opacity-60" : ""}`}
                          data-testid={`sc-row-${r.id}`}>
                        <Td>
                          <span className="tabular-nums font-semibold text-slate-500">{idx + 1}</span>
                          {r.id === comparison?.winner_id && <Trophy size={11} className="inline ml-1 text-amber-600"/>}
                        </Td>
                        <Td>
                          <button onClick={() => onNavigateTab?.(STRATEGY_TAB[r.id] || r.id)}
                                  className="font-semibold text-slate-900 hover:text-amber-700">
                            {r.label}
                          </button>
                          {!r.eligible && <span className="ml-2 text-[9px] uppercase text-slate-400">ineligible</span>}
                        </Td>
                        <Td align="right">
                          <span className="tabular-nums font-bold text-slate-900">
                            {r.eligible ? (r.final_score?.toFixed?.(1) ?? r.final_score) : "—"}
                          </span>
                        </Td>
                        <Td align="right"><span className="tabular-nums">${fmt(raw.projected_profit)}</span></Td>
                        <Td align="right"><span className="tabular-nums">${fmt(raw.monthly_cash_flow)}</span></Td>
                        <Td align="right"><span className="tabular-nums">${fmt(raw.cash_required)}</span></Td>
                        <Td align="right">
                          <span className="tabular-nums text-slate-700">{(r.confidence * 100).toFixed(0)}%</span>
                        </Td>
                        <Td>
                          <button onClick={() => setExpanded((e) => ({ ...e, [r.id]: !isOpen }))}
                                  data-testid={`sc-expand-${r.id}`}
                                  className="inline-flex items-center gap-1 text-[10px] uppercase text-slate-500 hover:text-slate-800">
                            {isOpen ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
                            details
                          </button>
                        </Td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-slate-100 bg-slate-50/50" data-testid={`sc-details-${r.id}`}>
                          <td colSpan={8} className="px-4 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                  Score Contribution
                                </div>
                                {Object.entries(r.score_contributions || {}).map(([k, v]) => (
                                  <ContribBar key={k} label={FACTOR_LABEL[k] || k} value={v}
                                              max={100 * (weights?.[k] || 1)}/>
                                ))}
                              </div>
                              <div>
                                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                  Details
                                </div>
                                <KV k="Reason" v={r.reason || "—"}/>
                                <KV k="Source" v={r.source_analysis_id ? `v${r.source_version || "?"} · ${(r.source_analysis_id || "").slice(0, 8)}…` : "no saved analysis"}/>
                                <KV k="Risk Score" v={raw.risk_score != null ? Number(raw.risk_score).toFixed(2) : "—"}/>
                                <KV k="Time to Exit" v={raw.time_to_exit_months != null ? `${raw.time_to_exit_months} mo` : "—"}/>
                                <KV k="Data Gaps" v={(r.data_gaps || []).length === 0 ? "none" : (r.data_gaps || []).join(", ")}/>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state helper */}
      {eligible.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="sc-empty">
          <AlertCircle size={20} className="mx-auto text-slate-400"/>
          <div className="mt-2 text-sm text-slate-700">
            No saved strategy analyses yet on this property.
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Click a strategy tab (Fix & Flip · Rental · BRRRR · Seller Finance · Subject-To · Wholetail · Deal Intelligence) and save at least one scenario.
          </div>
        </div>
      )}

      {/* Missing strategies chip row */}
      {ineligible.length > 0 && eligible.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sc-missing">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Ineligible or missing ({ineligible.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {ineligible.map((r) => (
              <button key={r.id}
                      onClick={() => onNavigateTab?.(STRATEGY_TAB[r.id] || r.id)}
                      data-testid={`sc-missing-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-xs px-3 py-1.5 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-800">
                <AlertCircle size={11}/> {r.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────── helpers ─────────────── */

function mapRankingToRanked(r) {
  return {
    id: r.strategy_id, label: r.strategy_label,
    eligible: r.eligible, final_score: r.final_score,
    factor_scores: r.factor_scores, score_contributions: r.score_contributions,
    raw_factors: r.raw_factors, confidence: r.confidence,
    data_gaps: r.data_gaps, reason: r.reason,
    source_analysis_id: r.source_analysis_id, source_version: r.source_version,
    source_tab: r.source_tab,
  };
}

function fmt(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

const Th = ({ children, align }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, align, className = "" }) => (
  <td className={`px-3 py-2 ${align === "right" ? "text-right" : ""} ${className}`}>{children}</td>
);
const KV = ({ k, v }) => (
  <div className="flex justify-between text-xs mb-1">
    <span className="text-slate-500">{k}</span>
    <span className="text-slate-900 tabular-nums text-right ml-2">{v}</span>
  </div>
);
const ContribBar = ({ label, value, max }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-[11px] mb-0.5">
        <span className="text-slate-600">{label}</span>
        <span className="text-slate-900 tabular-nums font-semibold">{Number(value).toFixed(1)}</span>
      </div>
      <div className="w-full h-1.5 rounded bg-slate-100 overflow-hidden">
        <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
};
